import React, { useEffect, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { BookOpen, Plus, Pencil, Trash2, Users, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { title: "", description: "", daysText: "" };

export const ReadingPlansPanel = ({ isSuper }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTheme, setAiTheme] = useState("");
  const [aiDays, setAiDays] = useState(30);
  const [aiBusy, setAiBusy] = useState("");
  const [aiPreview, setAiPreview] = useState(null); // { title, description, days } — not yet saved

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reading/admin/plans"); setPlans(data.plans || []); }
    catch { toast.error("Erreur de chargement"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh while any plan is being generated by the AI (background task).
  useEffect(() => {
    if (!plans.some((p) => p.ai_status === "generating")) return undefined;
    const iv = setInterval(async () => {
      try { const { data } = await api.get("/reading/admin/plans"); setPlans(data.plans || []); } catch { /* ignore */ }
    }, 6000);
    return () => clearInterval(iv);
  }, [plans]);

  const closeAi = () => { setAiOpen(false); setAiPreview(null); setAiTheme(""); setAiBusy(""); };

  const aiPreviewGen = async () => {
    if (!aiTheme.trim()) { toast.error("Indique un thème"); return; }
    setAiBusy("preview");
    try {
      const { data } = await api.post("/reading/admin/plans/ai-preview", { theme: aiTheme.trim(), days: Math.max(1, Math.min(366, parseInt(aiDays, 10) || 30)) });
      setAiPreview(data.preview);
    } catch (e) { toast.error(e?.friendlyMessage || "La génération IA a échoué. Réessaie."); }
    finally { setAiBusy(""); }
  };

  const aiPublish = async () => {
    if (!aiPreview) return;
    if (!aiPreview.title.trim()) { toast.error("Titre requis"); return; }
    setAiBusy("publish");
    try {
      await api.post("/reading/admin/plans", {
        title: aiPreview.title.trim(),
        description: (aiPreview.description || "").trim(),
        days: aiPreview.days,
        active: true,
      });
      toast.success("Plan publié 🎉");
      closeAi(); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setAiBusy(""); }
  };

  const aiGenerate = async (p) => {
    setAiBusy(p.id);
    try {
      await api.post(`/reading/admin/plans/${p.id}/ai-generate`, { days: p.total_days || (p.days || []).length || 30 });
      toast.success(`Génération des lectures de « ${p.title} » en cours…`);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setAiBusy(""); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "",
      description: p.description || "",
      daysText: (p.days || []).map((d) => d.refs).join("\n"),
    });
    setOpen(true);
  };

  const buildDays = () =>
    form.daysText.split("\n").map((l) => l.trim()).filter(Boolean).map((refs, i) => ({ day: i + 1, refs }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    const days = buildDays();
    if (days.length === 0) { toast.error("Ajoute au moins un jour de lecture"); return; }
    setBusy(true);
    try {
      const payload = { title: form.title.trim(), description: form.description.trim(), days };
      if (editing) await api.patch(`/reading/admin/plans/${editing.id}`, payload);
      else await api.post("/reading/admin/plans", { ...payload, active: true });
      toast.success(editing ? "Plan mis à jour" : "Plan créé");
      setOpen(false); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (p, active) => {
    setPlans((prev) => prev.map((x) => (x.id === p.id ? { ...x, active } : x)));
    try { await api.patch(`/reading/admin/plans/${p.id}`, { active }); }
    catch { toast.error("Erreur"); load(); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le plan « ${p.title} » ? Les progressions des membres seront perdues.`)) return;
    try { await api.delete(`/reading/admin/plans/${p.id}`); toast.success("Plan supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-reading-panel">
      <div className="yalola-card p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-bold font-display flex items-center gap-2"><BookOpen size={18} /> Plans de lecture biblique</div>
          <p className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>Active/désactive chaque plan. Un plan désactivé n'apparaît pas pour les membres (tu peux le lancer plus tard).</p>
        </div>
        {isSuper && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="pill" onClick={() => setAiOpen(true)} data-testid="reading-ai-create-btn"><Sparkles size={16} className="mr-1" style={{ color: "var(--yalola-primary)" }} /> IA : créer un plan</Button>
            <Button className="pill-primary" onClick={openCreate} data-testid="reading-create-btn"><Plus size={16} className="mr-1" /> Nouveau plan</Button>
          </div>
        )}
      </div>

      <div className="yalola-card divide-y" style={{ borderColor: "var(--yalola-border)" }}>
        {loading && <AdminSkeleton />}
        {!loading && plans.length === 0 && <div className="p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>Aucun plan.</div>}
        {plans.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`reading-row-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate flex items-center gap-2">
                {p.title}
                {p.active === false && <span className="chip text-[10px]" style={{ background: "var(--yalola-surface-alt)" }}>Désactivé</span>}
                {p.ai_status === "generating" && <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`reading-ai-generating-${p.id}`}><Loader2 size={10} className="animate-spin" /> IA en cours…</span>}
                {p.ai_status === "error" && <span className="chip text-[10px]" style={{ background: "#FEE2E2", color: "#DC2626" }} data-testid={`reading-ai-error-${p.id}`}>Échec IA</span>}
              </div>
              <div className="text-[11px] flex items-center gap-2 mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
                <span>{p.total_days} jours</span>
                <span className="inline-flex items-center gap-1"><Users size={11} /> {p.subscriber_count || 0}</span>
              </div>
            </div>
            {isSuper && (
              <div className="flex items-center gap-2 shrink-0">
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Générer les lectures avec l'IA" onClick={() => aiGenerate(p)} disabled={aiBusy === p.id || p.ai_status === "generating"} data-testid={`reading-ai-${p.id}`}>
                  {aiBusy === p.id || p.ai_status === "generating" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} style={{ color: "var(--yalola-primary)" }} />}
                </Button>
                <Switch checked={p.active !== false} onCheckedChange={(v) => toggleActive(p, v)} data-testid={`reading-active-${p.id}`} />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`reading-edit-${p.id}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(p)} data-testid={`reading-delete-${p.id}`}><Trash2 size={15} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="reading-plan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le plan" : "Nouveau plan de lecture"}</DialogTitle>
            <DialogDescription>Une ligne = un jour de lecture (ex. « Genèse 1–3, Matthieu 1 »). Les jours sont numérotés automatiquement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Les Évangiles en 30 jours" data-testid="reading-form-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Courte description" data-testid="reading-form-desc" />
            </div>
            <div>
              <Label>Jours de lecture (une ligne par jour)</Label>
              <Textarea rows={8} value={form.daysText} onChange={(e) => setForm({ ...form, daysText: e.target.value })} placeholder={"Genèse 1–3\nGenèse 4–6\nGenèse 7–9"} className="font-mono text-xs" data-testid="reading-form-days" />
              <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{buildDays().length} jour(s)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button className="pill-primary" onClick={save} disabled={busy} data-testid="reading-form-save">{busy ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI-create-plan modal — step 1: theme/duration, step 2: preview before publishing */}
      <Dialog open={aiOpen} onOpenChange={(v) => { if (!v) closeAi(); }}>
        <DialogContent className="max-w-lg" data-testid="reading-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={18} style={{ color: "var(--yalola-primary)" }} /> Créer un plan avec l'IA</DialogTitle>
            <DialogDescription>
              {aiPreview
                ? "Relis le plan généré. Tu peux ajuster le titre et la description, puis publie-le (il sera visible par les membres)."
                : "Donne un thème et une durée. L'IA rédige le titre, la description et les lectures jour par jour. Tu pourras tout relire avant de publier."}
            </DialogDescription>
          </DialogHeader>

          {!aiPreview ? (
            <>
              <div className="space-y-3">
                <div>
                  <Label>Thème du plan</Label>
                  <Input value={aiTheme} onChange={(e) => setAiTheme(e.target.value)} placeholder="Ex. La vie de Jésus, Les Psaumes de louange, La foi…" data-testid="reading-ai-theme" />
                </div>
                <div>
                  <Label>Durée (jours)</Label>
                  <Input type="number" min="1" max="366" value={aiDays} onChange={(e) => setAiDays(e.target.value)} className="max-w-[140px]" data-testid="reading-ai-days" />
                </div>
                <p className="text-[11px] flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>La génération consomme le crédit IA (clé Universelle). Rien n'est enregistré tant que tu n'as pas publié.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeAi} disabled={aiBusy === "preview"}>Annuler</Button>
                <Button className="pill-primary" onClick={aiPreviewGen} disabled={aiBusy === "preview"} data-testid="reading-ai-preview-btn">
                  {aiBusy === "preview" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />} {aiBusy === "preview" ? "Génération…" : "Générer l'aperçu"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-3" data-testid="reading-ai-preview">
                <div>
                  <Label>Titre</Label>
                  <Input value={aiPreview.title} onChange={(e) => setAiPreview({ ...aiPreview, title: e.target.value })} data-testid="reading-ai-preview-title" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea rows={2} value={aiPreview.description || ""} onChange={(e) => setAiPreview({ ...aiPreview, description: e.target.value })} data-testid="reading-ai-preview-desc" />
                </div>
                <div>
                  <Label>Lectures ({(aiPreview.days || []).length} jour{(aiPreview.days || []).length > 1 ? "s" : ""})</Label>
                  <div className="mt-1 rounded-lg border max-h-64 overflow-y-auto divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid="reading-ai-preview-days">
                    {(aiPreview.days || []).map((d) => (
                      <div key={d.day} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                        <span className="shrink-0 w-14 text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Jour {d.day}</span>
                        <span className="truncate">{d.refs}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="ghost" onClick={() => setAiPreview(null)} disabled={aiBusy === "publish"} data-testid="reading-ai-regenerate">← Modifier le thème</Button>
                <Button variant="outline" onClick={closeAi} disabled={aiBusy === "publish"}>Annuler</Button>
                <Button className="pill-primary" onClick={aiPublish} disabled={aiBusy === "publish"} data-testid="reading-ai-publish">
                  {aiBusy === "publish" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <BookOpen size={15} className="mr-1.5" />} Publier le plan
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReadingPlansPanel;
